import { useEffect, useState } from "react";
import { Copy, ShieldCheck, UserPlus, XCircle } from "lucide-react";
import { toast } from "sonner";
import { adminAPI } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  TailAdminEmptyState,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const AdminStaffInvitations = () => {
  const [form, setForm] = useState({ name: "", email: "", role_key: "operations_admin", expiry_hours: 24 });
  const [invitations, setInvitations] = useState([]);
  const [acceptPath, setAcceptPath] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => adminAPI.listStaffInvitations()
    .then((response) => setInvitations(response.data))
    .catch(() => toast.error("Unable to load staff invitations"));

  useEffect(() => { load(); }, []);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setAcceptPath("");
    try {
      const response = await adminAPI.createStaffInvitation(form);
      setAcceptPath(`${window.location.origin}${response.data.accept_path}`);
      setForm({ name: "", email: "", role_key: "operations_admin", expiry_hours: 24 });
      toast.success("Invitation created. Share the link privately.");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to create invitation");
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(acceptPath);
    toast.success("Private invitation link copied");
  };

  const revoke = async (invitationId) => {
    try {
      await adminAPI.revokeStaffInvitation(invitationId);
      toast.success("Invitation revoked");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to revoke invitation");
    }
  };

  const status = (invitation) => {
    if (invitation.accepted_at) return ["Accepted", "success"];
    if (invitation.revoked_at) return ["Revoked", "danger"];
    if (new Date(invitation.expires_at) <= new Date()) return ["Expired", "warning"];
    return ["Pending", "neutral"];
  };

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Access governance"
        title="Staff invitations"
        description="Invite staff with a scoped role. Recipients choose their password privately from a one-time link."
      />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,420px)_1fr]">
        <TailAdminPanel title="Create invitation" description="Only send the resulting link through a private channel.">
          <form className="space-y-4" onSubmit={submit}>
            <div><Label htmlFor="staff-name">Name</Label><Input id="staff-name" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label htmlFor="staff-email">Email</Label><Input id="staff-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div>
              <Label htmlFor="staff-role">Role</Label>
              <select id="staff-role" className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.role_key} onChange={(e) => setForm({ ...form, role_key: e.target.value })}>
                <option value="operations_admin">Operations admin</option>
                <option value="finance_operator">Finance operator</option>
                <option value="support_agent">Support agent</option>
                <option value="analyst">Analyst</option>
                <option value="auditor">Auditor</option>
                <option value="superadmin">Superadmin</option>
              </select>
            </div>
            <div><Label htmlFor="staff-expiry">Expires in hours</Label><Input id="staff-expiry" required type="number" min="1" max="168" value={form.expiry_hours} onChange={(e) => setForm({ ...form, expiry_hours: Number(e.target.value) })} /></div>
            <Button disabled={saving} className="w-full" type="submit"><UserPlus className="mr-2 h-4 w-4" />{saving ? "Creating…" : "Create private link"}</Button>
          </form>
          {acceptPath ? (
            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-medium text-amber-950">This link is shown once. Copy it now.</p>
              <p className="mt-2 break-all text-xs text-amber-900" data-testid="invitation-link">{acceptPath}</p>
              <Button type="button" variant="outline" className="mt-3" onClick={copy}><Copy className="mr-2 h-4 w-4" />Copy link</Button>
            </div>
          ) : null}
        </TailAdminPanel>
        <TailAdminPanel title="Invitation history" description="Tokens are never displayed again after creation.">
          {invitations.length ? <div className="space-y-3">{invitations.map((invitation) => {
            const [label, tone] = status(invitation);
            return <div key={invitation.invitation_id} className="flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between">
              <div><p className="font-medium text-gray-900">{invitation.name}</p><p className="text-sm text-gray-500">{invitation.email} · {invitation.role_key}</p><p className="text-xs text-gray-400">Expires {new Date(invitation.expires_at).toLocaleString()}</p></div>
              <div className="flex items-center gap-2"><TailAdminStatusBadge tone={tone}>{label}</TailAdminStatusBadge>{label === "Pending" ? <Button type="button" variant="outline" size="sm" onClick={() => revoke(invitation.invitation_id)}><XCircle className="mr-1 h-4 w-4" />Revoke</Button> : null}</div>
            </div>;
          })}</div> : <TailAdminEmptyState icon={ShieldCheck} title="No staff invitations" description="Create the first private invitation when a staff member needs access." />}
        </TailAdminPanel>
      </div>
    </div>
  );
};

export default AdminStaffInvitations;
